import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  TextFieldProps,
  useTheme,
} from '@mui/material';

import {
  getQuiztopiyaCurriculumsForAdmin,
  getQuiztopiyaState,
  getQuiztopiyaSubTopicsForAdmin,
  getQuiztopiyaTopicsForAdmin,
  resetAdminCurriculums,
  resetAdminSubTopics,
  resetAdminTopics,
} from '../../../../../../store/V2/QuiztopiyaSlice';
import {
  ISectionForAdmin,
  TCreateCourseResourceSectionAsAdmin,
  TUpdateCourseResourceSectionAsAdmin,
  createNewCourseResourceSectionAsAdmin,
  updateCourseResourceSectionAsAdmin,
} from '../../../../../../store/V2/SectionSlice';
import { getCoursesState } from '../../../../../../store/V2/CoursesSlice';
import { useAppSelector } from '../../../../../../hooks';
import { TSubsectionCreateNewItemRequestData } from '../../components/SectionItem';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';

interface AddResourceSectionItemModalProps {
  open: boolean;
  onClose: () => void;
  parentSectionItemData?: TSubsectionCreateNewItemRequestData['parentSection'];
  mode?: 'edit';
  section?: ISectionForAdmin;
}

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  selectedCurriculum: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  selectedSubject: TSelectOption | null;
  selectedSubjectLevel: TSelectOption | null;
  selectedTopic: TSelectOption | null;
  selectedSubTopic: TSelectOption | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  selectedCurriculum: null,
  selectedGrade: null,
  selectedSubject: null,
  selectedSubjectLevel: null,
  selectedTopic: null,
  selectedSubTopic: null,
};

const AddResourceSectionItemModal: React.FC<AddResourceSectionItemModalProps> = ({
  open,
  onClose,
  parentSectionItemData,
  mode,
  section,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // Store
  const quiztopiyaState = useAppSelector(getQuiztopiyaState);
  const coursesState = useAppSelector(getCoursesState);

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);
  const [subjectOptions, setSubjectOptions] = useState<Array<TSelectOption>>(
    []
  );

  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);

  const [subjectLevelOptions, setSubjectLevelOptions] = useState<
    Array<TSelectOption>
  >([]);

  const [topicOptions, setTopicOptions] = useState<Array<TSelectOption>>([]);

  const [subTopicOptions, setSubTopicOptions] = useState<Array<TSelectOption>>(
    []
  );

  const [isLoading, setLoading] = useState(false);

  const courseData = coursesState.adminCourse;
  const isSubmitDisabled =
    !state.selectedCurriculum ||
    !state.selectedGrade ||
    !state.selectedSubject ||
    !state.selectedSubjectLevel ||
    !state.selectedTopic ||
    !state.selectedSubTopic;

  useEffect(() => {
    dispatch(getQuiztopiyaCurriculumsForAdmin());

    if (mode === 'edit' && section) {
      setState((prevState) => {
        const _newState: TState = { ...prevState };

        if (section.curriculum) {
          _newState.selectedCurriculum = {
            label: section.curriculum,
            value: section.curriculum,
          };

          if (section.grade) {
            _newState.selectedGrade = {
              label: section.grade,
              value: section.grade,
            };

            if (section.subject && section.subjectCode) {
              _newState.selectedSubject = {
                label: section.subject,
                value: section.subjectCode,
              };

              if (section.subjectLevel) {
                _newState.selectedSubjectLevel = {
                  label: section.subjectLevel,
                  value: section.subjectLevel,
                };

                dispatch(
                  getQuiztopiyaTopicsForAdmin({
                    subjectCode: section.subjectCode,
                    subjectLevel: section.subjectLevel,
                  })
                );

                if (section.quiztopiyaTopicId && section.quiztopiyaTopicName) {
                  _newState.selectedTopic = {
                    label: section.quiztopiyaTopicName,
                    value: section.quiztopiyaTopicId,
                  };

                  dispatch(
                    getQuiztopiyaSubTopicsForAdmin({
                      topicId: section.quiztopiyaTopicId,
                    })
                  );

                  if (
                    section.quiztopiyaSubTopicId &&
                    section.quiztopiyaSubTopicName
                  ) {
                    _newState.selectedSubTopic = {
                      label: section.quiztopiyaSubTopicName,
                      value: section.quiztopiyaSubTopicId,
                    };
                  }
                }
              }
            }
          }
        }

        return _newState;
      });
    }

    return () => {
      dispatch(resetAdminCurriculums());
      dispatch(resetAdminTopics());
      dispatch(resetAdminSubTopics());
    };
  }, []);

  useEffect(() => {
    if (
      quiztopiyaState.adminCurriculums &&
      quiztopiyaState.adminCurriculums.length
    ) {
      const newCurriculumsOptions: TSelectOption[] = [];
      const newGradeOptions: TSelectOption[] = [];
      const newSubjectOptions: TSelectOption[] = [];
      const newSubjectLevelOptions: TSelectOption[] = [];

      quiztopiyaState.adminCurriculums.forEach((curriculumItem) => {
        newCurriculumsOptions.push({
          label: curriculumItem.name,
          value: curriculumItem.name,
        });
      });

      if (state.selectedCurriculum) {
        const matchingCurriculumItem = quiztopiyaState.adminCurriculums.find(
          (item) => item.name === state.selectedCurriculum?.value
        );

        if (
          matchingCurriculumItem &&
          matchingCurriculumItem.grades &&
          matchingCurriculumItem.grades.length
        ) {
          matchingCurriculumItem.grades.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.name,
              value: gradeItem.name,
            });
          });

          if (state.selectedGrade) {
            const matchingGradeItem = matchingCurriculumItem.grades.find(
              (item) => item.name === state.selectedGrade?.value
            );

            if (
              matchingGradeItem &&
              matchingGradeItem.subjects &&
              matchingGradeItem.subjects.length
            ) {
              matchingGradeItem.subjects.forEach((subjectItem) => {
                newSubjectOptions.push({
                  label: subjectItem.name,
                  value: subjectItem.code,
                });
              });

              if (state.selectedSubject) {
                const matchingSubjectItem = matchingGradeItem.subjects.find(
                  (item) => item.code === state.selectedSubject?.value
                );

                if (
                  matchingSubjectItem &&
                  matchingSubjectItem.subjectLevels &&
                  matchingSubjectItem.subjectLevels.length
                ) {
                  matchingSubjectItem.subjectLevels.forEach(
                    (subjectLevelItem) => {
                      newSubjectLevelOptions.push({
                        label: subjectLevelItem.name,
                        value: subjectLevelItem.name,
                      });
                    }
                  );
                }
              }
            }
          }
        }
      }

      setCurriculumOptions(newCurriculumsOptions);
      setGradeOptions(newGradeOptions);
      setSubjectOptions(newSubjectOptions);
      setSubjectLevelOptions(newSubjectLevelOptions);
    }
  }, [quiztopiyaState.adminCurriculums]);

  useEffect(() => {
    if (quiztopiyaState.adminTopics && quiztopiyaState.adminTopics.length) {
      const newTopicOptions: TSelectOption[] = [];

      quiztopiyaState.adminTopics.forEach((topicItem) => {
        newTopicOptions.push({
          label: topicItem.name,
          value: topicItem._id,
        });
      });

      setTopicOptions(newTopicOptions);
    }
  }, [quiztopiyaState.adminTopics]);

  useEffect(() => {
    if (
      quiztopiyaState.adminSubTopics &&
      quiztopiyaState.adminSubTopics.length
    ) {
      const newSubTopicOptions: TSelectOption[] = [];

      quiztopiyaState.adminSubTopics.forEach((subTopicItem) => {
        newSubTopicOptions.push({
          label: subTopicItem.name,
          value: subTopicItem._id,
        });
      });

      setSubTopicOptions(newSubTopicOptions);
    }
  }, [quiztopiyaState.adminSubTopics]);

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedCurriculum: value,
      selectedGrade: null,
      selectedSubject: null,
      selectedSubjectLevel: null,
      selectedTopic: null,
      selectedSubTopic: null,
    }));

    const newGradeOptions: TSelectOption[] = [];

    if (value) {
      const matchingCurriculumItem = quiztopiyaState.adminCurriculums.find(
        (item) => item.name === value.value
      );

      if (
        matchingCurriculumItem &&
        matchingCurriculumItem.grades &&
        matchingCurriculumItem.grades.length
      ) {
        matchingCurriculumItem.grades.forEach((gradeItem) => {
          newGradeOptions.push({
            label: gradeItem.name,
            value: gradeItem.name,
          });
        });
      }
    }

    setGradeOptions(newGradeOptions);
    setSubjectOptions([]);
    setSubjectLevelOptions([]);
    setTopicOptions([]);
    setSubTopicOptions([]);

    dispatch(resetAdminTopics());
    dispatch(resetAdminSubTopics());
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedGrade: value,
      selectedSubject: null,
      selectedSubjectLevel: null,
      selectedTopic: null,
      selectedSubTopic: null,
    }));

    const newSubjectOptions: TSelectOption[] = [];

    if (state.selectedCurriculum) {
      const matchingCurriculumItem = quiztopiyaState.adminCurriculums.find(
        (item) => item.name === state.selectedCurriculum?.value
      );

      if (
        value &&
        matchingCurriculumItem &&
        matchingCurriculumItem.grades &&
        matchingCurriculumItem.grades.length
      ) {
        const matchingGradeItem = matchingCurriculumItem.grades.find(
          (item) => item.name === value.value
        );

        if (
          matchingGradeItem &&
          matchingGradeItem.subjects &&
          matchingGradeItem.subjects.length
        ) {
          matchingGradeItem.subjects.forEach((subjectItem) => {
            newSubjectOptions.push({
              label: subjectItem.name,
              value: subjectItem.code,
            });
          });
        }
      }
    }

    setSubjectOptions(newSubjectOptions);
    setSubjectLevelOptions([]);
    setTopicOptions([]);
    setSubTopicOptions([]);

    dispatch(resetAdminTopics());
    dispatch(resetAdminSubTopics());
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedSubject: value,
      selectedSubjectLevel: null,
      selectedTopic: null,
      selectedSubTopic: null,
    }));

    const newSubjectLevelOptions: TSelectOption[] = [];

    if (state.selectedCurriculum) {
      const matchingCurriculumItem = quiztopiyaState.adminCurriculums.find(
        (item) => item.name === state.selectedCurriculum?.value
      );

      if (
        state.selectedGrade &&
        matchingCurriculumItem &&
        matchingCurriculumItem.grades &&
        matchingCurriculumItem.grades.length
      ) {
        const matchingGradeItem = matchingCurriculumItem.grades.find(
          (item) => item.name === state.selectedGrade?.value
        );

        if (
          value &&
          matchingGradeItem &&
          matchingGradeItem.subjects &&
          matchingGradeItem.subjects.length
        ) {
          const matchingSubjectItem = matchingGradeItem.subjects.find(
            (item) => item.code === value.value
          );

          if (
            matchingSubjectItem &&
            matchingSubjectItem.subjectLevels &&
            matchingSubjectItem.subjectLevels.length
          ) {
            matchingSubjectItem.subjectLevels.forEach((subjectLevelItem) => {
              newSubjectLevelOptions.push({
                label: subjectLevelItem.name,
                value: subjectLevelItem.name,
              });
            });
          }
        }
      }
    }

    setSubjectLevelOptions(newSubjectLevelOptions);
    setTopicOptions([]);
    setSubTopicOptions([]);

    dispatch(resetAdminTopics());
    dispatch(resetAdminSubTopics());
  };

  const handleSubjectLevelChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedSubjectLevel: value,
      selectedTopic: null,
      selectedSubTopic: null,
    }));

    setTopicOptions([]);
    setSubTopicOptions([]);

    if (value && state.selectedSubject) {
      dispatch(
        getQuiztopiyaTopicsForAdmin({
          subjectCode: state.selectedSubject.value,
          subjectLevel: value.label,
        })
      );
    }

    dispatch(resetAdminTopics());
    dispatch(resetAdminSubTopics());
  };

  const handleTopicChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedTopic: value,
      selectedSubTopic: null,
    }));

    if (value) {
      dispatch(
        getQuiztopiyaSubTopicsForAdmin({
          topicId: value.value,
        })
      );
    }

    setSubTopicOptions([]);

    dispatch(resetAdminSubTopics());
  };

  const handleSubTopicChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedSubTopic: value,
    }));
  };

  const handleSubmit = async () => {
    if (
      state.selectedCurriculum &&
      state.selectedGrade &&
      state.selectedSubject &&
      state.selectedSubjectLevel &&
      state.selectedTopic &&
      state.selectedSubTopic &&
      courseData
    ) {
      if (mode === 'edit' && section) {
        setLoading(true);

        const apiData: TUpdateCourseResourceSectionAsAdmin = {
          _id: section._id,
          curriculum: state.selectedCurriculum.value,
          grade: state.selectedGrade.value,
          subject: state.selectedSubject.label,
          subjectCode: state.selectedSubject.value,
          subjectLevel: state.selectedSubjectLevel.value,
          quiztopiyaTopicId: state.selectedTopic.value,
          quiztopiyaSubTopicId: state.selectedSubTopic.value,
          quiztopiyaTopicName: state.selectedTopic.label,
          quiztopiyaSubTopicName: state.selectedSubTopic.label,
        };

        await dispatch(updateCourseResourceSectionAsAdmin(apiData));

        setLoading(false);
      } else if (mode !== 'edit' && parentSectionItemData && courseData) {
        setLoading(true);

        const apiData: TCreateCourseResourceSectionAsAdmin = {
          parent: parentSectionItemData._id,
          sortingKey: parentSectionItemData.nextSortingKey,
          course: courseData._id,
          curriculum: state.selectedCurriculum.value,
          grade: state.selectedGrade.value,
          subject: state.selectedSubject.label,
          subjectCode: state.selectedSubject.value,
          subjectLevel: state.selectedSubjectLevel.value,
          quiztopiyaTopicId: state.selectedTopic.value,
          quiztopiyaSubTopicId: state.selectedSubTopic.value,
          quiztopiyaTopicName: state.selectedTopic.label,
          quiztopiyaSubTopicName: state.selectedSubTopic.label,
        };

        await dispatch(createNewCourseResourceSectionAsAdmin(apiData));

        setLoading(false);
      }
    }

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {mode === 'edit' ? 'Edit Resource' : 'Add Resource'}
      </DialogTitle>
      <DialogContent sx={{ pt: `${theme.spacing(2)} !important` }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedCurriculum}
                options={curriculumOptions}
                onChange={handleCurriculumChange}
                popupIcon={TPAutoCompleteArrowIconComponent}
                loading={quiztopiyaState.status === 'loading'}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.value
                }
                getOptionKey={(option: TSelectOption) => option.value}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Curriculum"
                    placeholder="Curriculum"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedGrade}
                options={gradeOptions}
                onChange={handleGradeChange}
                disabled={!state.selectedCurriculum}
                popupIcon={TPAutoCompleteArrowIconComponent}
                loading={quiztopiyaState.status === 'loading'}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.value
                }
                getOptionKey={(option: TSelectOption) => option.value}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Grade"
                    placeholder="Grade"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedSubject}
                options={subjectOptions}
                onChange={handleSubjectChange}
                disabled={!state.selectedGrade}
                popupIcon={TPAutoCompleteArrowIconComponent}
                loading={quiztopiyaState.status === 'loading'}
                getOptionKey={(option: TSelectOption) => option.value}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Subject"
                    placeholder="Subject"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedSubjectLevel}
                options={subjectLevelOptions}
                onChange={handleSubjectLevelChange}
                disabled={!state.selectedSubject}
                popupIcon={TPAutoCompleteArrowIconComponent}
                loading={quiztopiyaState.status === 'loading'}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.value
                }
                getOptionKey={(option: TSelectOption) => option.value}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Subject Level"
                    placeholder="Subject Level"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedTopic}
                options={topicOptions}
                onChange={handleTopicChange}
                disabled={!state.selectedSubjectLevel}
                popupIcon={TPAutoCompleteArrowIconComponent}
                loading={quiztopiyaState.status === 'loading'}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Topic"
                    placeholder="Topic"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedSubTopic}
                options={subTopicOptions}
                onChange={handleSubTopicChange}
                disabled={!state.selectedTopic}
                popupIcon={TPAutoCompleteArrowIconComponent}
                loading={quiztopiyaState.status === 'loading'}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Subtopic"
                    placeholder="Subtopic"
                  />
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px' }}>
        <LoadingButton
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          loading={isLoading}
        >
          {mode === 'edit' ? 'Save' : 'Add'}
        </LoadingButton>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddResourceSectionItemModal;
